import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../../../../utils/echarts'
import { Icon } from '../../../../../atoms/Icon'
import {
  Card,
  CardHeader,
  CardTitle,
  ChartContainer,
} from '../../../BuildingCharacteristics.styles'
import { CHART_COLORS } from '../NeighborhoodContextTab.constants'
import type { AgeDistributionChartProps } from '../NeighborhoodContextTab.types'

export interface AgeDistributionChartRef {
  exportChart: () => string | null
}

export const AgeDistributionChart = forwardRef<
  AgeDistributionChartRef,
  AgeDistributionChartProps
>(({ neighborhoodAnalysis, t }, ref) => {
  const ageDistributionChartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<echarts.ECharts | null>(null)

  useImperativeHandle(ref, () => ({
    exportChart: () => {
      if (chartInstance.current) {
        return chartInstance.current.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff',
        })
      }
      return null
    },
  }))

  useEffect(() => {
    if (
      ageDistributionChartRef.current &&
      neighborhoodAnalysis?.statistics?.age_distribution
    ) {
      if (chartInstance.current) {
        chartInstance.current.dispose()
      }
      chartInstance.current = safeChartInit(ageDistributionChartRef.current)
      const ageData = neighborhoodAnalysis.statistics.age_distribution

      const option = {
        animation: true,
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
        },
        grid: {
          left: '10%',
          right: '10%',
          bottom: '15%',
          top: '10%',
        },
        xAxis: {
          type: 'category',
          data: [
            t('neighborhood.ageRanges.pre1919'),
            t('neighborhood.ageRanges.1919_1945'),
            t('neighborhood.ageRanges.1946_1960'),
            t('neighborhood.ageRanges.1961_1970'),
            t('neighborhood.ageRanges.1971_1980'),
            t('neighborhood.ageRanges.1981_1990'),
            t('neighborhood.ageRanges.1991_2000'),
            t('neighborhood.ageRanges.2001_2010'),
            t('neighborhood.ageRanges.2011_2020'),
            t('neighborhood.ageRanges.post2020'),
          ],
          axisLabel: {
            rotate: 45,
            fontSize: 10,
          },
        },
        yAxis: {
          type: 'value',
          name: t('neighborhood.chart.buildingsAxis'),
        },
        series: [
          {
            type: 'bar',
            data: [
              ageData.pre_1919,
              ageData['1919_1945'],
              ageData['1946_1960'],
              ageData['1961_1970'],
              ageData['1971_1980'],
              ageData['1981_1990'],
              ageData['1991_2000'],
              ageData['2001_2010'],
              ageData['2011_2020'],
              ageData.post_2020,
            ],
            itemStyle: {
              color: CHART_COLORS.BUILDING,
            },
            label: {
              show: true,
              position: 'top',
              fontSize: 10,
            },
          },
        ],
      }
      chartInstance.current.setOption(option)

      const handleResize = () => chartInstance.current?.resize()
      window.addEventListener('resize', handleResize)
      return () => {
        window.removeEventListener('resize', handleResize)
        chartInstance.current?.dispose()
      }
    }
  }, [neighborhoodAnalysis, t])

  if (!neighborhoodAnalysis.statistics?.age_distribution) {
    return null
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <Icon name="bar-chart-2" size="small" />
          {t('neighborhood.cards.ageDistribution.title')}
        </CardTitle>
      </CardHeader>
      <ChartContainer
        ref={ageDistributionChartRef}
        style={{ height: '300px' }}
      />
    </Card>
  )
})

AgeDistributionChart.displayName = 'AgeDistributionChart'
